import ym from 'js.api';
import * as qs from 'querystring';
import * as URL from 'url';
import { UrlParameters } from '../interfaces/UrlParameters';
import { hasUrlParameters, isNotEmptyString } from './guards';
import { getConfig } from '../ConfigLoader';

export function capitalize(str: string): string {
	return str.substring(0, 1).toUpperCase() + str.substring(1).toLowerCase();
}

export function pointEqZero(point: ym.provider.PointLiteral): boolean {
	return point.x === 0 && point.y === 0;
}

export function pointNeqZero(point: ym.provider.PointLiteral): boolean {
	return point.x !== 0 && point.y !== 0;
}

export function latLngEqZero(latlng: ym.provider.LatLngLiteral): boolean {
	return (
		Number.EPSILON > latlng.lat &&
		Number.EPSILON > latlng.lng &&
		-Number.EPSILON < latlng.lat &&
		-Number.EPSILON < latlng.lng
	);
}

export function latLngNeqZero(latlng: ym.provider.LatLngLiteral): boolean {
	return (
		(latlng.lat > Number.EPSILON || latlng.lat < -Number.EPSILON) &&
		(latlng.lng > Number.EPSILON || latlng.lng < -Number.EPSILON)
	);
}

export function hasIntersection(a: string[], b: string[]): boolean {
	for (const x of a) {
		for (const y of b) {
			if (x === y) {
				return true;
			}
		}
	}
	return false;
}

let params: UrlParameters | undefined;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function writeUrlParams(params: UrlParameters): UrlParameters {
	return {};
}

export function readUrlParams(): UrlParameters {
	if (!getConfig().history) {
		return {};
	}
	if (hasUrlParameters(params)) {
		return params;
	}

	const queryStr = URL.parse(location.href).query || '';
	const queryParams: qs.ParsedUrlQuery = qs.parse(queryStr);
	params = {};

	if (isNotEmptyString(queryParams.lat) && isNotEmptyString(queryParams.lng)) {
		params.center = {
			lat: parseFloat(queryParams.lat || '0'),
			lng: parseFloat(queryParams.lng || '0')
		};
		if (isNotEmptyString(queryParams.alt)) {
			params.center.alt = parseFloat(queryParams.alt || '0');
		}
	}

	if (isNotEmptyString(queryParams.bounds)) {
		const bounds = queryParams.bounds.split(',');
		if (bounds.length === 4) {
			const [lng1, lat1, lng2, lat2] = bounds;
			params.bounds = {
				northEast: { lat: parseFloat(lat2), lng: parseFloat(lng1) },
				southWest: { lat: parseFloat(lat1), lng: parseFloat(lng2) }
			};
		}
	}

	if (isNotEmptyString(queryParams.id)) {
		params.id = queryParams.id;
	}

	if (isNotEmptyString(queryParams.s)) {
		params.searchTerm = queryParams.s;
	}

	if (isNotEmptyString(queryParams.f)) {
		params.branches = queryParams.f.split(',');
	}

	return params;
}

export function decodeHTML(str: string): string | null {
	const parser = new DOMParser();
	const dom = parser.parseFromString(`<!doctype html><body>${str}</body>`, 'text/html');
	return dom.body.textContent;
}

interface AddressFormatOld {
	Country?: string;
	Zip?: string;
	City?: string;
	CityPart?: string;
	CityAddOn?: string;
	Street?: string;
	StreetNo?: string;
	HouseNo?: string;
	Description?: string;
}

interface AddressFormatNew {
	country?: string;
	zip?: string;
	city?: string;
	cityPart?: string;
	cityAddOn?: string;
	street?: string;
	streetNo?: string;
	houseNo?: string;
	description?: string;
}

interface FormatAddressLineOptions {
	prependCountry?: boolean;
	avoidZip?: boolean;
}

const _formatZipCity = function (zip: string | void, city: string | void, cityaddon: string | void, citypart: string | void, country: string | void, avoidZip = false, prependCountry = false) {
	const zipCity = [avoidZip ? '' : zip, city, cityaddon].filter(s => s).join(' ');
	const cityline = zipCity && citypart ? `${zipCity} (${citypart})` : zipCity || '';
	if (prependCountry) {
		let isoCountry = country;
		if (country === 'DE') {
			isoCountry = 'D';
		} else if (country === 'AT') {
			isoCountry = 'A';
		}
		return isoCountry + '-' + cityline;
	}
	return cityline;
}

const _formatStreetNo = function (street: string | void, houseNo: string | void, streetNo: string | void) {
	if (street && houseNo) {
		return [street, houseNo].filter(s => s).join(' ') || '';
	}
	return streetNo || '';
}

export const formatStreetNo = function (addressObj: AddressFormatNew | AddressFormatOld | string): string {
	if ('object' === typeof addressObj && null !== addressObj) {
		if ('Street' in addressObj || 'StreetNo' in addressObj) {
			return _formatStreetNo(addressObj.Street, addressObj.HouseNo, addressObj.StreetNo);
		}
		if ('street' in addressObj || 'streetNo' in addressObj) {
			return _formatStreetNo(addressObj.street, addressObj.houseNo, addressObj.streetNo);
		}
	}
	return addressObj as string;
}

export const formatZipCity = function (addressObj: AddressFormatNew | AddressFormatOld | string, avoidZip = false, prependCountry = false): string {
	if ('object' === typeof addressObj && null !== addressObj) {
		if ('Zip' in addressObj) {
			return _formatZipCity(addressObj.Zip, addressObj.City, addressObj.CityAddOn, addressObj.CityPart, addressObj.Country, avoidZip, prependCountry);
		}
		if ('zip' in addressObj) {
			return _formatZipCity(addressObj.zip, addressObj.city, addressObj.cityAddOn, addressObj.cityPart, addressObj.country, avoidZip, prependCountry);
		}
	}
	return addressObj as string;
}

export function formatAddressLine(
	addressObj: AddressFormatNew | AddressFormatOld | string,
	options: FormatAddressLineOptions = {}
): string {
	if ('string' === typeof addressObj) {
		return addressObj;
	}

	const results = [
		formatStreetNo(addressObj),
		formatZipCity(addressObj, options.avoidZip, options.prependCountry),
	];

	return results.filter(s => s).join(', ');
}
